import React from "react";
import Link from "next/link";
import Icon from "./Icon";
import Button from "./Button";
import LangSwitcher from "./LangSwitcher";
import MobileDrawer from "./MobileDrawer";
import renderIcon from "../../../components/renderIcon";
import { useOverflowNav } from "../../../components/useOverflowNav";
import { applyLang } from "../../../i18n";
import { buildDynamicArticlesMenuItems, buildContentHubHomeUrlForSite } from "../../../components/ContentHub/utils";
import { buildMenuUrl, isExternalUrl, buildLinkHref } from "../../../components/normalizeMenuUrl";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";

// Récursif : chaque niveau s'affiche en dropdown.
// - Niveau 0 (racine) : dropdown sous l'item parent.
// - Niveau >= 1 : sous-dropdown à droite (cascade), avec flip auto si pas de place.
// Le placement est recalculé à l'ouverture (hover/focus) via measure du viewport.
function NavItem({ item, fullMenu, org, lang, depth = 0, contentHubCategories }) {
    const localized = applyLang(item, lang);
    const isDynamicArticles = item.type === "DYNAMIC_ARTICLES";
    const rawUrl = (localized.url || "").trim();

    // Menu dynamique : l'item parent redirige vers la home ContentHub
    // (/jewish-educations) ET ouvre le sous-menu des catégories. On injecte tout
    // l'arbre des catégories (récursif) dans le fullMenu pour que la cascade
    // existante rende les sous-catégories imbriquées sur toute la profondeur.
    let effectiveFullMenu = fullMenu;
    let dynamicUrl = null;
    if (isDynamicArticles) {
        const site = contentHubCategories?.site;
        dynamicUrl = buildContentHubHomeUrlForSite(org, site);
        const virtualItems = buildDynamicArticlesMenuItems(
            contentHubCategories?.items, item.id, site, org
        );
        // Les enfants directs de l'item dynamique = catégories racine ; les niveaux
        // plus profonds sont reliés entre eux par parent dans virtualItems.
        effectiveFullMenu = [...fullMenu, ...virtualItems];
    }

    // Un item est placeholder (#) s'il n'a ni URL ni cible dynamique.
    const isPlaceholder = isDynamicArticles
        ? false
        : (!rawUrl || rawUrl === "#");
    const url = isDynamicArticles
        ? dynamicUrl
        : (isPlaceholder ? "#" : buildMenuUrl(rawUrl, org));

    const children = effectiveFullMenu.filter(i => i.parent === item.id);

    const groupRef = React.useRef(null);
    const subRef = React.useRef(null);
    // "placement" gère les flips :
    // - niveau 0 : 'bottom-start' | 'bottom-end' (flip horizontal)
    // - niveau ≥1 : 'right-start' | 'left-start' (flip latéral) + 'up' si flip vertical
    const [placement, setPlacement] = React.useState(depth === 0 ? "bottom-start" : "right-start");
    const [verticalFlip, setVerticalFlip] = React.useState(false);

    const recompute = React.useCallback(() => {
        if (!groupRef.current || !subRef.current) return;
        if (typeof window === "undefined") return;

        const isRtl = document.documentElement.getAttribute("dir") === "rtl";
        const groupRect = groupRef.current.getBoundingClientRect();
        // Mesure naturelle du sous-menu (avant contrainte)
        const subRect = subRef.current.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const MARGIN = 8;

        if (depth === 0) {
            // Root : aligné au début de l'item ; flip end si ça déborde à droite (LTR) ou à gauche (RTL)
            let next = "bottom-start";
            if (!isRtl) {
                const overflowRight = groupRect.left + subRect.width + MARGIN > vw;
                if (overflowRight) next = "bottom-end";
            } else {
                const overflowLeft = groupRect.right - subRect.width - MARGIN < 0;
                if (overflowLeft) next = "bottom-end";
            }
            setPlacement(next);
        } else {
            // Niveaux imbriqués : par défaut vers l'extérieur (droite en LTR / gauche en RTL)
            let next = "right-start";
            if (!isRtl) {
                const spaceRight = vw - groupRect.right - MARGIN;
                if (spaceRight < subRect.width) next = "left-start";
            } else {
                const spaceLeft = groupRect.left - MARGIN;
                if (spaceLeft < subRect.width) next = "left-start";
            }
            setPlacement(next);
        }

        // Flip vertical : si le sous-menu débordait en bas, on remonte
        const overflowBottom = groupRect.top + subRect.height + MARGIN > vh;
        const spaceAbove = groupRect.bottom;
        setVerticalFlip(overflowBottom && spaceAbove > subRect.height);
    }, [depth]);

    const onOpen = React.useCallback(() => {
        // rAF pour mesurer après le display:flex
        if (typeof window !== "undefined") {
            window.requestAnimationFrame(recompute);
        }
    }, [recompute]);

    React.useEffect(() => {
        if (typeof window === "undefined") return;
        const onResize = () => recompute();
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [recompute]);

    if (children.length === 0) {
        const cls = depth === 0 ? "tr-nav-link" : "tr-nav-sub__item";
        if (isPlaceholder) {
            return (
                <span className={`${cls} tr-nav-link--placeholder`} role={depth === 0 ? undefined : "menuitem"}>
                    {localized.name}
                </span>
            );
        }
        if (depth === 0) {
            return (
                <Link href={buildLinkHref(url, org)} legacyBehavior>
                    <a className={cls}>{localized.name}</a>
                </Link>
            );
        }
        return (
            <Link href={buildLinkHref(url, org)} legacyBehavior>
                <a className={cls} role="menuitem">
                    {localized.name}
                </a>
            </Link>
        );
    }

    const groupCls = depth === 0 ? "tr-nav-group" : "tr-nav-group tr-nav-group--nested";
    const subCls = [
        depth === 0 ? "tr-nav-sub" : "tr-nav-sub tr-nav-sub--nested",
        `tr-nav-sub--${placement}`,
        verticalFlip ? "tr-nav-sub--up" : "",
    ].filter(Boolean).join(" ");
    const triggerCls = depth === 0
        ? "tr-nav-link tr-nav-link--has-sub"
        : "tr-nav-sub__item tr-nav-sub__item--has-sub";

    const triggerContent = (
        <>
            <span className={depth === 0 ? "tr-nav-link__label" : "tr-nav-sub__label"}>{localized.name}</span>
            <Icon name={depth === 0 ? "chevronDown" : "chevronRight"} size={14} />
        </>
    );

    return (
        <div
            className={groupCls}
            ref={groupRef}
            onMouseEnter={onOpen}
            onFocus={onOpen}
        >
            {isPlaceholder ? (
                <span className={`${triggerCls} tr-nav-link--placeholder`} tabIndex={0}>
                    {triggerContent}
                </span>
            ) : (
                <Link href={buildLinkHref(url, org)} legacyBehavior>
                    <a className={triggerCls}>{triggerContent}</a>
                </Link>
            )}
            <div className={subCls} role="menu" ref={subRef}>
                {children.map(child => (
                    <NavItem
                        key={child.id}
                        item={child}
                        fullMenu={effectiveFullMenu}
                        org={org}
                        lang={lang}
                        depth={depth + 1}
                        contentHubCategories={contentHubCategories}
                    />
                ))}
            </div>
        </div>
    );
}

// Au-delà de ce nombre de liens de 1er niveau visibles dans la nav desktop,
// on considère que c'est un « grand menu » et on étire le header à 90vw
// (classe .tr-header--wide) pour accueillir un max de liens sans serrage.
const WIDE_MENU_THRESHOLD = 10;

export default function Header({ context }) {
    const { org, config, travel, contentHubCategories } = context;
    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const navRef = React.useRef(null);
    const headerRef = React.useRef(null);

    // Signature du menu : change quand le nombre de liens, leur config ou la
    // langue (donc les libellés/largeurs) changent → déclenche un recalcul de
    // largeur. (org.limited masque tout le menu sauf "Accueil".)
    const navSignature = [
        (org.web_menu || []).filter(i => !i.parent).length,
        config["menu.horaire.active"] !== false,
        config["menu.event.active"] !== false,
        !!org.hasPrefabFetes,
        !!org.limited,
        context.lang?.value || context.lang,
    ].join("|");

    // Étire le header à 90vw quand la nav contient >= WIDE_MENU_THRESHOLD liens
    // de 1er niveau. On compte les enfants directs de la nav (chaque .tr-nav-link
    // ou .tr-nav-group = 1 lien visible), ce qui couvre menu CMS + liens fixes
    // activés (Horaires, Événements…) sans dupliquer leur logique d'affichage.
    // Déclaré AVANT useOverflowNav pour que la classe soit posée avant la mesure
    // de largeur (le hook lit la largeur dispo, qui dépend de --wide).
    // useEffect (et non useLayoutEffect) pour rester SSR-safe.
    React.useEffect(() => {
        const nav = navRef.current;
        const header = headerRef.current;
        if (!nav || !header) return;
        header.classList.toggle("tr-header--wide", nav.children.length >= WIDE_MENU_THRESHOLD);
    }, [navSignature]);

    // Tronque dynamiquement les liens qui ne tiennent pas (étire d'abord à 90vw
    // via --wide, puis masque le surplus → accessible dans le burger/drawer).
    useOverflowNav(navRef, navSignature);

    // Les dropdowns s'ouvrent au :hover/:focus-within (CSS pur). Comme Next.js
    // navigue sans recharger la page, après un clic la souris reste sur l'item
    // et le menu resterait ouvert. Au clic sur un lien, on ajoute une classe qui
    // force la fermeture (override du hover), puis on blur pour tuer :focus-within.
    // La classe est retirée dès que la souris quitte la nav → hover rétabli.
    const handleNavClick = React.useCallback((e) => {
        const link = e.target.closest("a");
        if (!link || !navRef.current || !navRef.current.contains(link)) return;
        // Liens placeholder (#) : ne ferment pas, ils servent juste à ouvrir le sous-menu.
        const href = link.getAttribute("href");
        if (href === "#") return;
        navRef.current.classList.add("tr-nav--suppress");
        if (typeof link.blur === "function") link.blur();
    }, []);

    const handleNavLeave = React.useCallback(() => {
        navRef.current?.classList.remove("tr-nav--suppress");
    }, []);

    if (context.render === "embed") return null;

    const fullMenu = org.web_menu || [];
    fullMenu.sort((a,b)=>a.position-b.position);
    const menu = fullMenu.filter(i => !i.parent);

    // Boutons configurables (max 2) stockés sous travel.menu.button_{N}_{label|url|icon}.
    // Le label est multilingue (résolu via applyLang), url/icon communs à toutes les langues.
    const localizedMenu = applyLang(travel?.menu || {}, context.lang);
    const menuButtons = [1, 2]
        .map((n) => ({
            label: localizedMenu[`button_${n}_label`],
            url: localizedMenu[`button_${n}_url`],
            icon: localizedMenu[`button_${n}_icon`],
        }))
        .filter((b) => b.label && b.url);

    return (
        <>
            <header className="tr-header-wrap">
                <div className="tr-header" ref={headerRef}>
                    {/* Logo */}
                    <Link href={config["orgLogoUrl"] || org.BASE_WEB_URL} legacyBehavior>
                        <a className="tr-header__brand tr-focusable" aria-label={org.name}>
                            {org.logo
                                ? <img src={org.logo} alt={org.name} />
                                : <span style={{ fontWeight: 700, fontSize: 16 }}>{org.name}</span>
                            }
                        </a>
                    </Link>

                    {/* Nav desktop */}
                    <nav
                        className="tr-header__nav"
                        aria-label={t`Navigation principale`}
                        ref={navRef}
                        onClick={handleNavClick}
                        onMouseLeave={handleNavLeave}
                    >
                        {!org.limited && menu.map(item => (
                            <NavItem
                                key={item.id}
                                item={item}
                                fullMenu={fullMenu}
                                org={org}
                                lang={context.lang}
                                contentHubCategories={contentHubCategories}
                            />
                        ))}

                        {!org.limited && config["menu.horaire.active"] !== false && (
                            <Link href={org.BASE_WEB_URL + "/travel/horaires"} legacyBehavior>
                                <a className="tr-nav-link"><Trans>Horaires</Trans></a>
                            </Link>
                        )}

                        {!org.limited && config["menu.event.active"] !== false && (
                            <Link href={org.BASE_WEB_URL + "/evenements"} legacyBehavior>
                                <a className="tr-nav-link"><Trans>Événements</Trans></a>
                            </Link>
                        )}

                        {/* Fêtes préfabriquées (système prefab_events) : entrée auto,
                            visible seulement quand l'org a des fêtes publiées à venir
                            (flag org.hasPrefabFetes, calculé côté API avec le cache org). */}
                        {!org.limited && org.hasPrefabFetes && (
                            <Link href={org.BASE_WEB_URL + "/travel/fetes"} legacyBehavior>
                                <a className="tr-nav-link"><Trans>Fêtes</Trans></a>
                            </Link>
                        )}

                        {org.limited && (
                            <Link href={org.BASE_WEB_URL} legacyBehavior>
                                <a className="tr-nav-link"><Trans>Accueil</Trans></a>
                            </Link>
                        )}
                    </nav>

                    {/* Outils : boutons configurables puis fallbacks Don/Shabbat si rien configuré */}
                    <div className="tr-header__tools">
                        {!org.limited && menuButtons.length > 0 && menuButtons.slice(0, 2).map((btn, i) => {
                            const url = buildMenuUrl(btn.url, org);
                            // Externe = absolu qui n'a pas été réécrit vers le domaine custom.
                            const isExternal = isExternalUrl(url, org);
                            if (isExternal) {
                                return (
                                    <Button key={i} variant={i === 0 ? "primary" : "secondary"} size="sm" as="a" href={url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                                        {btn.icon && renderIcon(btn.icon, 14)}
                                        <span className="tr-btn-label">{btn.label}</span>
                                    </Button>
                                );
                            }
                            return (
                                <Link key={i} href={url} legacyBehavior>
                                    <Button variant={i === 0 ? "primary" : "secondary"} size="sm" as="span">
                                        {btn.icon && renderIcon(btn.icon, 14)}
                                        <span className="tr-btn-label">{btn.label}</span>
                                    </Button>
                                </Link>
                            );
                        })}

                        {!org.limited && menuButtons.length === 0 && config["menu.don.active"] !== false && (
                            <Link href={org.BASE_WEB_URL + "/don"} legacyBehavior>
                                <Button variant="primary" size="sm" as="span">
                                    <Icon name="heart" size={14} />
                                    <span className="tr-btn-label"><Trans>Don</Trans></span>
                                </Button>
                            </Link>
                        )}

                        {!org.limited && menuButtons.length === 0 && config["menu.horaire.active"] !== false && (
                            <Link href={org.BASE_WEB_URL + "/travel/horaires"} legacyBehavior>
                                <Button variant="secondary" size="sm" as="span">
                                    <Icon name="candles" size={14} />
                                    <span className="tr-btn-label"><Trans>Shabbat</Trans></span>
                                </Button>
                            </Link>
                        )}

                        <LangSwitcher context={context} />

                        <button
                            className="tr-header__burger tr-focusable"
                            aria-label={t`Ouvrir le menu`}
                            onClick={() => setDrawerOpen(true)}
                        >
                            <Icon name="menu" size={18} />
                        </button>
                    </div>
                </div>
            </header>

            <MobileDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                context={context}
            />
        </>
    );
}
